import { useEffect, useState } from 'react';
import { getOperaciones, descargarExcelOperacion } from '../services/api';

interface OperacionRun {
  id: number;
  agenciaNombre: string;
  fechaEjecucion: string;
  estatus: number;
  totalPolizas: number;
  totalPendientes: number;
  filePath: string;
}

const toISOWeek = (date: Date): string => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  const weekNum = 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
};

const weekLabel = (isoWeek: string): string => {
  const [year, wPart] = isoWeek.split('-W');
  const week = parseInt(wPart);
  // Get Monday of that week
  const jan4 = new Date(parseInt(year), 0, 4);
  const monday = new Date(jan4.getTime() + (week - 1) * 7 * 86400000 - ((jan4.getDay() + 6) % 7) * 86400000);
  const sunday = new Date(monday.getTime() + 6 * 86400000);
  const fmt = (d: Date) => d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  return `${fmt(monday)} – ${fmt(sunday)}`;
};

export default function Operaciones() {
  const [runs, setRuns] = useState<OperacionRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [semana, setSemana] = useState(() => toISOWeek(new Date()));

  useEffect(() => {
    getOperaciones()
      .then(res => setRuns(res.data))
      .catch(() => setError('No se pudo cargar el historial de operaciones'))
      .finally(() => setLoading(false));
  }, []);

  // Distinct weeks present in data, descending
  const semanas = Array.from(new Set(runs.map(r => toISOWeek(new Date(r.fechaEjecucion))))).sort().reverse();

  const filtered = semana
    ? runs.filter(r => toISOWeek(new Date(r.fechaEjecucion)) === semana)
    : runs;

  const descargar = async (id: number, agencia: string) => {
    try {
      const res = await descargarExcelOperacion(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `polizas_${agencia}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('No se pudo descargar el archivo');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Operaciones</h1>
          <p className="text-gray-500 text-sm mt-1">Historial de corridas del scraper por agencia</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 font-medium">Semana:</label>
          <select
            value={semana}
            onChange={e => setSemana(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">Todas</option>
            {semanas.map(s => (
              <option key={s} value={s}>
                {weekLabel(s)} ({s})
              </option>
            ))}
          </select>
          {semana && (
            <button
              onClick={() => setSemana('')}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Ver todas
            </button>
          )}
        </div>
      </div>

      {loading && <p className="text-gray-500">Cargando...</p>}
      {error && <p className="text-red-500">{error}</p>}

      {!loading && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left font-medium text-gray-600">Agencia</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Fecha</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Pólizas</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Pendientes</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Estatus</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Descargar</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    Sin registros para esta semana
                  </td>
                </tr>
              )}
              {filtered.map(run => (
                <tr key={run.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{run.agenciaNombre}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {new Date(run.fechaEjecucion).toLocaleString('es-MX', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{run.totalPolizas.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{run.totalPendientes.toLocaleString()}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      run.estatus === 1
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {run.estatus === 1 ? 'Completada' : 'Error'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => descargar(run.id, run.agenciaNombre)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      ⬇ Excel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
